import { useState, useRef, useMemo, useCallback } from 'react';
import {
  pointerWithin,
  rectIntersection,
  closestCenter,
  getFirstCollision,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  CollisionDetection,
  DroppableContainer,
} from '@dnd-kit/core';
import { FullProject, Task, LogEntry } from '../types';
import { markGlobalDragStart, markGlobalDragEnd } from '../utils/dragGuardState';
import { canUserEditTask, canUserMoveTaskBetweenProjects } from '../utils/projectPermissions';
import { resolveScopedTaskById } from '../utils/idNormalizer';
import { STANDARD_DATE_BUCKET_IDS } from '../domain/taskStatusAndTypes';

export const ALL_DND_COLUMN_IDS: readonly string[] = [
  'open',
  'todo',
  'in_progress',
  'hold',
  'completed',
  'contact',
  'archive',
  'pool',
  'pending',
  'archived',
  ...STANDARD_DATE_BUCKET_IDS,
  'later',
  'undated',
];

export type AppDndActive = {
  id: string | number;
  data?: { current?: { task?: Task; projectId?: string; [key: string]: unknown } | unknown };
  rect?: unknown;
  node?: unknown;
  [key: string]: unknown;
};
export type AppDndOver = {
  id: string | number;
  data?: { current?: { task?: Task; projectId?: string; [key: string]: unknown } | unknown };
  rect?: unknown;
  node?: unknown;
  [key: string]: unknown;
} | null;

export interface AppDragStartEvent {
  active: AppDndActive;
  [key: string]: unknown;
}

export interface AppDragOverEvent {
  active: AppDndActive;
  over?: AppDndOver;
  [key: string]: unknown;
}

export interface AppDragEndEvent {
  active: AppDndActive;
  over?: AppDndOver;
  [key: string]: unknown;
}

export interface UseAppDndCoordinatorProps {
  isProjectEditor: boolean;
  allProjects?: FullProject[];
  currentUserId?: string | null;
  userEmail?: string | null;
  customAlert: (title: string, message: string, type?: 'success' | 'error' | 'info', confirmText?: string) => Promise<void> | void | unknown;
  viewDragHandlersRef: {
    current: {
      onDragStart?: (event: DragStartEvent) => void;
      onDragEnd?: (event: DragEndEvent) => boolean | void;
      onDragOver?: (event: DragOverEvent) => void;
      onDragCancel?: (event?: unknown) => void;
      [key: string]: unknown;
    } | null;
  };
  setShowDragDropHint: (show: boolean) => void;
  allTasksGlobal: Task[];
  handleAssignTaskToProject: (taskId: string | number, targetProjectId: string) => void;
  updateTask: (id: string | number, updates: Partial<Task>, projectId?: string) => void;
  deleteTask: (id: string | number) => void;
  addLogEntry: (action: LogEntry['action'], description: string) => void;
  initialActiveNodeRectRef?: React.MutableRefObject<DOMRect | null>;
}

export function useAppDndCoordinator({
  isProjectEditor,
  allProjects,
  currentUserId,
  userEmail,
  customAlert,
  viewDragHandlersRef,
  setShowDragDropHint,
  allTasksGlobal,
  handleAssignTaskToProject,
  updateTask,
  deleteTask,
  addLogEntry,
  initialActiveNodeRectRef: externalRectRef
}: UseAppDndCoordinatorProps) {

  const internalRectRef = useRef<DOMRect | ClientRect | null>(null);
  const initialActiveNodeRectRef = externalRectRef || internalRectRef;

  const [activeDragId, setActiveDragId] = useState<string | number | null>(null);
  const [droppedZoneId, setDroppedZoneId] = useState<string | null>(null);
  const lastDragEndRef = useRef<number>(0);

  const resolveTaskByActiveId = useCallback(
    (
      rawActiveId: string | number | null | undefined,
      activeDataTask?: Task | null,
      preferredProjectId?: string | null
    ): Task | null =>
      resolveScopedTaskById(rawActiveId, {
        activeDataTask,
        projects: allProjects,
        tasksFallback: allTasksGlobal,
        preferredProjectId,
      }),
    [allProjects, allTasksGlobal]
  );

  const activeDragTask = useMemo(() => {
    if (!activeDragId) return null;
    return resolveTaskByActiveId(activeDragId);
  }, [activeDragId, resolveTaskByActiveId]);

  const handleDragStart = useCallback((event: AppDragStartEvent) => {
    const activeData = event.active?.data?.current as { task?: Task; projectId?: string } | undefined;
    const draggedTask = resolveTaskByActiveId(
      event.active?.id,
      activeData?.task,
      activeData?.projectId
    );
    // Guard: Block dragging entirely if the user is in read-only mode or the specific task is read-only (H-1)
    if (
      !isProjectEditor ||
      (draggedTask && !canUserEditTask(draggedTask, allProjects, currentUserId, userEmail))
    ) {
      customAlert("Nur Lesezugriff", "Sie haben in diesem Projekt nur Leserechte. Aufgaben können nicht verschoben werden.", "info");
      return;
    }

    const activeObj = event.active as { rect?: { current?: { initial?: DOMRect | null } }; node?: { current?: HTMLElement | null } };
    if (activeObj?.rect?.current?.initial) {
      initialActiveNodeRectRef.current = activeObj.rect.current.initial;
    } else if (activeObj?.node?.current?.getBoundingClientRect) {
      initialActiveNodeRectRef.current = activeObj.node.current.getBoundingClientRect();
    }

    setActiveDragId(event.active.id);
    markGlobalDragStart();
    viewDragHandlersRef.current?.onDragStart?.(event as unknown as DragStartEvent);

    const hintSeen = localStorage.getItem('stratum_dragdrop_hint_seen') === 'true';
    if (!hintSeen) {
      setShowDragDropHint(true);
    }
  }, [isProjectEditor, allProjects, currentUserId, userEmail, resolveTaskByActiveId, customAlert, viewDragHandlersRef, setShowDragDropHint, initialActiveNodeRectRef]);

  const handleDragOver = useCallback((event: AppDragOverEvent) => {
    // Delegate to the currently active view's drag-over handler (e.g. KanbanView)
    viewDragHandlersRef.current?.onDragOver?.(event as unknown as DragOverEvent);
  }, [viewDragHandlersRef]);

  const handleDragCancel = useCallback((event?: unknown) => {
    const now = Date.now();
    lastDragEndRef.current = now;
    markGlobalDragEnd(now);
    setActiveDragId(null);
    initialActiveNodeRectRef.current = null;
    if (viewDragHandlersRef.current?.onDragCancel) {
      viewDragHandlersRef.current.onDragCancel(event);
    } else if (event && typeof event === 'object' && 'active' in event) {
      viewDragHandlersRef.current?.onDragEnd?.({ ...(event as AppDragEndEvent), over: null } as unknown as DragEndEvent);
    }
  }, [viewDragHandlersRef, initialActiveNodeRectRef]);

  const handleDragEnd = useCallback((event: AppDragEndEvent) => {
    const now = Date.now();
    lastDragEndRef.current = now;
    markGlobalDragEnd(now);
    const { active, over } = event;
    setActiveDragId(null);
    initialActiveNodeRectRef.current = null; // Clear initial rect on drag end
    
    // Always notify the active view of drag-end first so it can clean up its local state (e.g. optimistic columns).
    // A view may return `true` to signal it fully handled the drop (incl. footer zones) – then we stop here.
    const handledByView = (viewDragHandlersRef.current?.onDragEnd?.(event as unknown as DragEndEvent) as unknown) === true;
    if (handledByView) return;

    // Guard: Block all state mutations on drop if the user is in read-only mode
    if (!isProjectEditor) {
      return;
    }

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;
    const activeData = active?.data?.current as { task?: Task; projectId?: string } | undefined;
    const findTargetTask = () =>
      resolveTaskByActiveId(
        activeId,
        activeData?.task,
        activeData?.projectId
      );
    const targetTask = findTargetTask();

    // H-1: Enforce per-task write permission in global views and dropzones
    if (targetTask && !canUserEditTask(targetTask, allProjects, currentUserId, userEmail)) {
      customAlert("Nur Lesezugriff", "Sie haben für diese Aufgabe nur Leserechte.", "info");
      return;
    }

    // Sidebar Drop Detection
    if (String(overId).startsWith('sidebar-project-')) {
      const targetProjectId = String(overId).replace('sidebar-project-', '');
      if (
        targetTask &&
        !canUserMoveTaskBetweenProjects(
          targetTask,
          targetProjectId,
          allProjects,
          currentUserId,
          userEmail
        )
      ) {
        customAlert(
          "Nur Lesezugriff",
          "Sie benötigen Schreibrechte im Quell- und Zielprojekt, um diese Aufgabe zu verschieben.",
          "info"
        );
        return;
      }
      handleAssignTaskToProject(activeId, targetProjectId);
      return;
    }

    // Sidebar Person Drop Detection
    if (String(overId).startsWith('assignee-')) {
      const personName = String(overId).replace('assignee-', '');
      const task = targetTask;
      if (task) {
        updateTask(task.id, { assignee: personName }, task.projectId);
        addLogEntry('update', `Aufgabe zu ${personName} zugewiesen`);
        setDroppedZoneId(String(overId));
        setTimeout(() => setDroppedZoneId(null), 800);
      }
      return;
    }

    // Delete Drop Detection
    if (String(overId) === 'action-delete') {
      const task = targetTask;
      if (task) {
        setDroppedZoneId('action-delete');
        deleteTask(task.id);
        setTimeout(() => setDroppedZoneId(null), 800);
      }
      return;
    }

    // Priority Drop Detection
    if (String(overId).startsWith('priority-')) {
      const priority = String(overId).replace('priority-', '') as Task['priority'];
      const task = targetTask;
      if (task) {
        updateTask(task.id, { priority }, task.projectId);
        setDroppedZoneId(String(overId));
        setTimeout(() => setDroppedZoneId(null), 800);
      }
      return;
    }

    // Timer Drop Detection (Zeiterfassung starten/stoppen)
    if (String(overId) === 'action-timer') {
      const task = targetTask;
      if (task) {
        const isRunning = !!(task.timerStartedAt && task.timerStartedAt > 0);
        if (isRunning) {
          const elapsed = Date.now() - task.timerStartedAt!;
          updateTask(task.id, { 
            timerStartedAt: null, 
            trackedTime: (task.trackedTime || 0) + elapsed 
          }, task.projectId);
          addLogEntry('update', `Zeiterfassung für "${task.name}" angehalten`);
        } else {
          updateTask(task.id, { 
            timerStartedAt: Date.now(),
            trackedTime: task.trackedTime || 0
          }, task.projectId);
          addLogEntry('update', `Zeiterfassung für "${task.name}" gestartet`);
        }
        setDroppedZoneId('action-timer');
        setTimeout(() => setDroppedZoneId(null), 800);
      }
      return;
    }

    // Status Drop Detection
    if (String(overId).startsWith('status-')) {
      const status = String(overId).replace('status-', '');
      const task = targetTask;
      if (task) {
        updateTask(task.id, { status, completed: status === 'completed' }, task.projectId);
        setDroppedZoneId(String(overId));
        setTimeout(() => setDroppedZoneId(null), 800);
      }
      return;
    }
  }, [handleAssignTaskToProject, updateTask, deleteTask, resolveTaskByActiveId, allProjects, currentUserId, userEmail, customAlert, addLogEntry, isProjectEditor, viewDragHandlersRef, initialActiveNodeRectRef]);

  const customCollisionDetection: CollisionDetection = useCallback((args) => {
    const isSpecialDropZone = (id: unknown) => {
      const s = String(id);
      return s.startsWith('priority-') || 
        s.startsWith('status-') || 
        s.startsWith('sidebar-project-') || 
        s.startsWith('assignee-') ||
        s === 'action-delete' ||
        s === 'action-timer';
    };

    // 0. If the pointer is above the columns (in the header bar/drop zones area),
    // prevent highlighting the columns below.
    if (args.pointerCoordinates) {
      const columnContainers = args.droppableContainers.filter(c => 
        ALL_DND_COLUMN_IDS.includes(String(c.id))
      );
      
      let columnsTop = Infinity;
      columnContainers.forEach(c => {
        const rectTop = c.rect?.current?.top;
        if (typeof rectTop === 'number' && rectTop < columnsTop) {
          // Subtract a 10px buffer to account for styling margins
          columnsTop = rectTop - 10;
        }
      });

      if (columnsTop !== Infinity && args.pointerCoordinates.y < columnsTop) {
        // The cursor is above the columns.
        // We only allow detecting collisions with the special drop zones (Priority, Status, Assignee, Sidebar, Delete).
        const pointerCollisions = pointerWithin(args);
        const specialPointerMatch = pointerCollisions.find(c => isSpecialDropZone(c.id));
        if (specialPointerMatch) return [specialPointerMatch];

        const rectCollisions = rectIntersection(args);
        const specialRectMatch = rectCollisions.find(c => isSpecialDropZone(c.id));
        if (specialRectMatch) return [specialRectMatch];

        // Suppress any column highlights
        return [];
      }
    }

    // 1. Check if the pointer is strictly within any of our special drop zones
    const pointerCollisions = pointerWithin(args);
    const specialPointerMatch = pointerCollisions.find(c => isSpecialDropZone(c.id));
    if (specialPointerMatch) return [specialPointerMatch];

    // 3. Official dnd-kit Multiple Containers Strategy
    // First, identify which column we are currently hovering over using pointerWithin
    const pointerIntersections = pointerWithin(args);
    const validPointerIntersections = pointerIntersections.filter(c => !isSpecialDropZone(c.id));
    
    let overId = getFirstCollision(validPointerIntersections, 'id');
    
    if (overId != null) {
      // Find the column this overId belongs to
      const overContainer = args.droppableContainers.find(c => c.id === overId);
      let targetColumnId = overId;
      
      if (overContainer?.data?.current?.columnId) {
        targetColumnId = overContainer.data.current.columnId;
      }
      
      // If we found a target column, isolate collision to that column
      if (targetColumnId) {
        const columnItems = args.droppableContainers.filter(c => 
          c.data?.current?.columnId === targetColumnId && c.id !== targetColumnId
        );
        
        // 1. Try pointerWithin against the column's items (best for varying heights)
        const itemIntersections = pointerWithin({
          ...args,
          droppableContainers: columnItems
        });
        
        if (itemIntersections.length > 0) {
          return itemIntersections;
        }

        // 2. Direct hit inside the column container (e.g. empty area, column background)
        const isPointerDirectlyInColumn = validPointerIntersections.some(c => String(c.id) === String(targetColumnId));
        if (isPointerDirectlyInColumn) {
          return [{ id: targetColumnId }];
        }

        // 3. Fallback to closestCenter, but we MUST include the column itself
        // so that dropping into an empty column works!
        const columnAndItems = args.droppableContainers.filter(c => 
          c.id === targetColumnId || (c.data?.current?.columnId === targetColumnId)
        );

        return closestCenter({
          ...args,
          droppableContainers: columnAndItems
        });
      }
    }

    // Fallback if no valid pointer intersection is found
    return closestCenter(args).filter(c => !isSpecialDropZone(c.id));
  }, []);

  return {
    activeDragId,
    setActiveDragId,
    activeDragTask,
    droppedZoneId,
    setDroppedZoneId,
    lastDragEndRef,
    initialActiveNodeRectRef,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    customCollisionDetection
  };
}
